import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { BASE_URL, CDN_URL, UPLOAD_URL } from "../config/apiConfig";

const TaskContext = createContext();

// CHANGED: Added a safe JSON parsing helper to prevent "Unexpected token '<'" crashes.
// This guards against cases where the backend returns an HTML error page (404/500/proxy errors)
// or an empty body instead of valid JSON, which previously caused res.json() to throw.
const safeParseJSON = async (res) => {
  try {
    const text = await res.text();
    if (!text) {
      return { status: res.status, message: "Empty response from server", data: null };
    }
    try {
      return JSON.parse(text);
    } catch (parseErr) {
      return { status: res.status, message: "Invalid response format from server", data: null };
    }
  } catch (readErr) {
    return { status: res.status || 500, message: "Failed to read server response", data: null };
  }
};

export const TaskProvider = ({ children }) => {
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  const [taskHoldStatus, setTaskHoldStatus] = useState({}); // { [taskId]: true/false }

  // Fetch tasks using fetch API
  // CHANGED: Removed toast.error calls — this is a GET/fetch operation (initial load), not a user action.
  const fetchTasks = useCallback(async () => {
  try {
    setLoading(true);

    const token = localStorage.getItem("token");
    if (!token) {
      setTasks([]);
      setFilteredTasks([]);
      return { success: false, message: "Not authenticated" };
    }

    const res = await fetch(`${BASE_URL}/tasks`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()
    if (data.status === 200 && Array.isArray(data.data)) {
      const apiData = data.data.map((t) => ({
        id: t.id,
        task_id: t.task_id,
        tid: t.tid,
        title: t.title,
        project_id: t.project_id,
        project_name: t.project_name ?? "",
        user_id: t.user_id,

        start_date: t.start_date,
        deployment_date: t.deployment_date,
        end_date: t.end_date,
        time: t.time,
        points: t.points,
        assigned_hours: t.assigned_hours,
        progress_history: t.progress_history || [],

        priority: t.priority || "Medium",

        // FIX: previously this key was declared twice in the same object literal
        // (once as `type: Array.isArray(t.tags) ? t.tags : []` for tags, and again
        // further down as `type: t.type ?? ""`). The second assignment silently
        // clobbered the first, so tags were always lost. Split into two distinct
        // keys: `tags` (array used by the UI) and `category` (raw t.type value).
        tags: Array.isArray(t.tags) ? t.tags : [],
        category: t.type ?? "",

        // normalize status for Kanban
        status: t.status && t.status.trim() !== "" ? t.status : "To Do",

        description: t.description || "",

        // normalize links
        links: Array.isArray(t.links)
          ? t.links
          : (t.links && t.links !== "" ? [t.links] : []),

        rightTag: t.priority || "General",
        extraTag: t.tag || null,

        // FIX: backend uses create_audit_time
        date: t.create_audit_time,

        // avatar handling
        avatar: t.profile
          ? `${CDN_URL}/${t.profile}`
          : "/avatar1.png",

        // optional but useful
        assignee_name: `${t.first_name ?? ""} ${t.last_name ?? ""}`.trim(),
        comments: t.comments ?? "",
        project_code: t.project_code ?? "",
        images: Array.isArray(t.images)
          ? t.images.map(img => `${CDN_URL}/${img}`)
          : [],
        status_history: t.status_history || [],
      }));

      setTasks(apiData);
      setFilteredTasks(apiData);
    } else {
      setTasks([]);
      setFilteredTasks([]);
      // CHANGED: removed toast.error(data.message || "Failed to fetch tasks")
    }
  } catch (error) {
    console.error("API Fetch Error:", error);
    setTasks([]);
    setFilteredTasks([]);
    // CHANGED: removed toast.error("Something went wrong while fetching tasks")
  } finally {
    setLoading(false);
  }
}, []);


  // ⭐ ADD TASK API (POST) — user action, toasts preserved
  const addTask = useCallback(async (payload) => {
  try {
    const token = localStorage.getItem("token");
    // get user from localStorage
    const user = JSON.parse(localStorage.getItem("user"));
    const createUserId = user?.id || null;

    // merge audit fields into payload
    const finalPayload = {
      ...payload,
      create_audit_id: createUserId,
    };

    const url = `${BASE_URL}/tasks`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(finalPayload),
    });

    const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

    if (res.ok && (data.status === 200 || data.status === 201)) {
      toast.success(data.message || "Task created successfully"); // KEPT: user-initiated action
    } else {
      toast.error(data.message || "Failed to create task"); // KEPT: user-initiated action
    }

    return data;
  } catch (error) {
    console.error("Add Task Error:", error);
    toast.error("Something went wrong while creating the task"); // KEPT: user-initiated action
    return null;
  }
}, [BASE_URL]);

// Update task — user action, toasts preserved
const updateTask = useCallback(async (taskId, payload) => {
  try {
    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user"));

    const createUserId = user?.id || null;

    const url = `${BASE_URL}/tasks/${taskId}`;

    // ✅ Add create_audit_id to payload
    const updatedPayload = {
      ...payload,
      create_audit_id: createUserId,
    };

    const res = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(updatedPayload),
    });

    const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

    // ✅ Update global context state
    if (data?.status === 200 && data?.task) {
      setTasks((prev) =>
        prev.map((task) =>
          task.id === taskId
            ? {
                ...task,
                ...data.task,
              }
            : task
        )
      );

      setFilteredTasks((prev) =>
        prev.map((task) =>
          task.id === taskId
            ? {
                ...task,
                ...data.task,
              }
            : task
        )
      );

      toast.success(data.message || "Task updated successfully"); // KEPT: user-initiated action
    } else {
      toast.error(data.message || "Failed to update task"); // KEPT: user-initiated action
    }

    return data;
  } catch (error) {
    toast.error("Something went wrong while updating the task"); // KEPT: user-initiated action
    return null;
  }
}, [BASE_URL]);


// Delete task — user action, toasts preserved
const deleteTask = async (taskId) => {
  try {
    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user"));
    const deleteUserId = user?.id || null;

    const payload = {
      delete_audit_id: deleteUserId,
    };

    const res = await fetch(`${BASE_URL}/tasks/${taskId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),  // 👈 sending JSON body
    });

    const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

    if (res.ok && data.status === 200) {
      toast.success(data.message || "Task deleted successfully"); // KEPT: user-initiated action
    } else {
      toast.error(data.message || "Failed to delete task"); // KEPT: user-initiated action
    }

    return data;
  } catch (error) {
    toast.error("Something went wrong while deleting the task"); // KEPT: user-initiated action
    return null;
  }
};

// Fetch task holds — GET/fetch operation, no toasts
// CHANGED: Removed toast.error call — this is a fetch operation, not a user action.
const fetchTaskHolds = useCallback(async (taskId) => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return { holds: [], isOnHold: false };

    const res = await fetch(`${BASE_URL}/task-holds/${taskId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

    if (data.status && Array.isArray(data.data)) {
      const latestHold = data.data[0];
      const isOnHold = latestHold?.is_on_hold || false;

      // ✅ update hold status state
      setTaskHoldStatus((prev) => ({
        ...prev,
        [taskId]: isOnHold,
      }));

      return { holds: data.data, isOnHold };
    }

    setTaskHoldStatus((prev) => ({ ...prev, [taskId]: false }));
    return { holds: [], isOnHold: false };
  } catch (error) {
    console.error("Fetch Task Holds Error:", error);
    setTaskHoldStatus((prev) => ({ ...prev, [taskId]: false }));
    // CHANGED: removed toast.error("Something went wrong while fetching task holds")
    return { holds: [], isOnHold: false };
  }
}, []);

// Toggle task hold — user action, toasts preserved
const toggleTaskHold = async (taskId) => {
  try {
    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user"));
    const userId = user?.id;

    if (!userId) return { success: false };

    // Call API to toggle hold
    const res = await fetch(`${BASE_URL}/task-holds`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ task_id: taskId, user_id: userId }),
    });

    const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

    if (!res.ok) {
      toast.error(data.message || "Failed to update task hold status"); // KEPT: user-initiated action
      return { success: false };
    }

    // Fetch the current hold status after toggle
    const holdRes = await fetchTaskHolds(taskId);

    toast.success(
      data.message ||
        (holdRes.isOnHold ? "Task placed on hold" : "Task hold removed")
    ); // KEPT: user-initiated action

    return { success: true, isHolding: holdRes.isOnHold, holdStart: holdRes.holdStart };
  } catch (error) {
    console.error(error);
    toast.error("Something went wrong while updating task hold status"); // KEPT: user-initiated action
    return { success: false };
  }
};


  // Load tasks automatically when context loads
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  return (
    <TaskContext.Provider
      value={{
        tasks,
        filteredTasks,
        setFilteredTasks,
        fetchTasks,
        addTask ,
        updateTask,
        loading,
        deleteTask,
        toggleTaskHold,
        fetchTaskHolds,
        taskHoldStatus
      }}
    >
      {children}
    </TaskContext.Provider>
  );
};

// Custom hook
export const useTaskContext = () => useContext(TaskContext);